import React, { useState } from 'react';
import ActivityItem from './ActivityItem';
import AddActivityButton from './AddActivityButton';
import ActivityTypeSelector from './ActivityTypeSelector';
import InlineActivityForm from './InlineActivityForm';

// Helper function to truncate text
const truncateText = (text: string, maxLength: number = 40): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

interface SectionCardProps {
  section: any;
  showTypeSelector: string | null;
  inlineEditingSectionId: string | null;
  editingSectionNameId: string | null;
  editingActivityId: string | null;
  selectedActivityId: string | null;
  newActivity: {
    title: string;
    type: string;
    description: string;
  };
  isSaving: boolean;
  onShowTypeSelector: (sectionId: string) => void;
  onSelectActivityType: (sectionId: string, type: string) => void;
  onCancelTypeSelector: () => void;
  onCancelInlineActivity: () => void;
  onSaveInlineActivity: (sectionId: string) => void;
  onActivityChange: (activity: any) => void;
  onEditSectionName: (sectionId: string) => void;
  onSaveSectionName: (sectionId: string, newName: string) => void;
  onCancelEditSectionName: () => void;
  onEditActivity: (activityId: string) => void;
  onSaveActivityTitle: (activityId: string, newTitle: string) => void;
  onCancelEditActivity: () => void;
  onDeleteSection: (sectionId: string) => void;
  onDeleteActivity: (activityId: string, sectionId: string) => void;
  onSelectActivity: (activityId: string) => void;
}

const SectionCard: React.FC<SectionCardProps> = ({
  section,
  showTypeSelector,
  inlineEditingSectionId,
  editingSectionNameId,
  editingActivityId,
  selectedActivityId,
  newActivity,
  isSaving,
  onShowTypeSelector,
  onSelectActivityType,
  onCancelTypeSelector,
  onCancelInlineActivity,
  onSaveInlineActivity,
  onActivityChange,
  onEditSectionName,
  onSaveSectionName,
  onCancelEditSectionName,
  onEditActivity,
  onSaveActivityTitle,
  onCancelEditActivity,
  onDeleteSection,
  onDeleteActivity,
  onSelectActivity
}) => {
  const [editedSectionName, setEditedSectionName] = useState(section.name);
  const [isExpanded, setIsExpanded] = useState(true);
  
  const handleSaveSectionName = () => {
    if (editedSectionName.trim()) {
      onSaveSectionName(section.section_id, editedSectionName);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveSectionName();
    } else if (e.key === 'Escape') {
      onCancelEditSectionName();
    }
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
      <div className="p-3 border-b border-gray-100">
        {/* First line - Title only */}
        <div className="flex items-start">
          <button 
            onClick={toggleExpand}
            className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none mt-1"
          >
            <svg 
              className={`w-4 h-4 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div className="flex-grow min-h-[24px] flex items-start relative">
            {editingSectionNameId === section.section_id ? (
              <input
                type="text"
                value={editedSectionName}
                onChange={(e) => setEditedSectionName(e.target.value)}
                onBlur={handleSaveSectionName}
                onKeyDown={handleKeyDown}
                className="absolute inset-0 text-sm font-medium border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ 
                  margin: 0, 
                  padding: '0 2px', 
                  minHeight: '24px',
                  lineHeight: '24px',
                  boxSizing: 'border-box',
                  width: '100%'
                }}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span 
                className="text-sm font-medium text-gray-900 cursor-text inline-block break-words"
                style={{ 
                  padding: '2px',
                  lineHeight: '1.3',
                  minHeight: '24px',
                  boxSizing: 'border-box',
                  wordBreak: 'break-word',
                  width: '100%'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditSectionName(section.section_id);
                }}
              >
                {truncateText(section.name)}
              </span>
            )}
          </div>
        </div>
        
        {/* Second line - Activity count and Actions */}
        <div className="flex items-center justify-between mt-3 ml-8">
          <div className="text-xs text-gray-500">
            {section.activities?.length || 0} hoạt động
          </div>
          <div className="flex items-center space-x-1">
            <button 
              className="p-1 text-gray-400 hover:text-blue-600"
              onClick={(e) => {
                e.stopPropagation();
                onEditSectionName(section.section_id);
              }}
              disabled={isSaving}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button 
              className="p-1 text-gray-400 hover:text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteSection(section.section_id);
              }}
              disabled={isSaving}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Activities */}
      {isExpanded && (
        <div className="px-3 py-2">
          <div className="ml-6 space-y-1">
            {section.activities?.map((activity: any) => (
              <ActivityItem 
                key={activity.activity_id} 
                activity={activity}
                isEditing={editingActivityId === activity.activity_id}
                isSelected={selectedActivityId === activity.activity_id}
                onEdit={onEditActivity}
                onSave={onSaveActivityTitle}
                onCancel={onCancelEditActivity}
                onDelete={(activityId) => onDeleteActivity(activityId, section.section_id)}
                onSelect={onSelectActivity}
              />
            ))}
            
            {/* Type Selector Dropdown */}
            {showTypeSelector === section.section_id && (
              <ActivityTypeSelector 
                onSelectActivityType={(type) => onSelectActivityType(section.section_id, type)}
                onCancel={onCancelTypeSelector}
              />
            )}
            
            {/* Inline Activity Form */}
            {inlineEditingSectionId === section.section_id ? (
              <InlineActivityForm
                activity={newActivity}
                onChange={onActivityChange}
                onSave={() => onSaveInlineActivity(section.section_id)}
                onCancel={onCancelInlineActivity}
                isSaving={isSaving}
              />
            ) : (
              <AddActivityButton 
                onClick={() => onShowTypeSelector(section.section_id)}
                disabled={isSaving || inlineEditingSectionId !== null || showTypeSelector !== null}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionCard; 